import {css, html} from '@isceco/widget-library2/external/lit'
import '@isceco/widget-library2/basic-elements/TextInput/TextInput.js'
import '@isceco/widget-library2/basic-elements/Title/Title.js'
import '@isceco/widget-library2/basic-elements/Button/Button.js'
import '@isceco/widget-library2/basic-elements/Form/Form.js'
import '@isceco/widget-library2/basic-elements/ModalDialog/ModalDialog.js'
import WebComponent from '../../WebComponent.js'
import I18n from '../../i18n.js'
import StammdatenService from '../../services/StammdatenService.js'
import AdminService from '../../services/AdminService.js'


export default class Parameter extends WebComponent {

  static get EVENT_KEYS() {
    return {
      RELOAD_TRIGGERED: 'parameter-reload-triggered'
    }
  }

  get css() {
    return css `
      isceco-title:first-of-type {
        display: block;
        margin-top: -20px;
        margin-bottom: -15px;
      }

      .title-content {
        display: flex;
        gap: 1em;
        margin-bottom: 0.5em;
        align-items: center;
      }
      .grid-container {
        display: grid;
        grid-template-columns: 1fr repeat(4, minmax(80px, 1fr));
        gap: 10px;
        align-items: center;
      }
      .grid-header {
        font-weight: bold;
        text-align: center;
      }
      .region {
        text-align: left;
      }
    `
  }

  get translationFile() {
    return './views/Admin/i18n.json'
  }

  constructor() {
    super()
    this.adminService = new AdminService()
    this.stammdatenService = new StammdatenService()
    this.data = {}
    this.bilanzZonen = {}
    this.nextUrl = ''
  }

  connectedCallback() {
    super.connectedCallback()

    this._refresh = () => {
      send(Parameter.EVENT_KEYS.RELOAD_TRIGGERED)
      this._loadData()
    }
    document.addEventListener(I18n.EVENT_KEYS.CHANGE_LANGUAGE, this._refresh)

    this._loadData()
  }

  disconnectedCallback() {
    document.removeEventListener(I18n.EVENT_KEYS.CHANGE_LANGUAGE, this._refresh)
  }

  _loadData() {
    Promise.all([
      this.stammdatenService.getBilanzzonen(),
      this.adminService.list('sigmoid-parameter')])
      .then(([bilanzZonen, sigmoidParams]) => {
        this.bilanzZonen = bilanzZonen.filter(zone => zone.code !== 'CH' && zone.code !== 'CHOT' && zone.code !== 'DCH');
        this.data = sigmoidParams
        this.render()
      }).catch(_ => this.reload())
  }

  getTemplate() {
    return html `
     <isceco-title text="${this.i18n.translate('admin.sigmoid.parameter.header')}">
       <nav class="parameter-title-navigation">
         <isceco-button
           id="parameter-speichern"
           variant="primary"
           size="small"
           icon="save"
           @click="${_ => {
             if(document.getElementById("parameter-form").validate()) {
               document.getElementById('parameter-confirm-dialog').removeAttribute('hidden')
             }
           }}"
         ></isceco-button>
       </nav>
      </isceco-title>

     ${this.data.length > 0 ?
        html `
          <p>${this.i18n.translate('admin.sigmoid.parameter.aktualisiert')} ${formatDateTime(this.data[0].audit.updateTime ? this.data[0].audit.updateTime : this.data[0].audit.creationTime)} </p>
        ` : html ``
      }

      <isceco-form id="parameter-form"
                   prevent-navigation
                   @abort="${event => this.handleAbort(event)}"
      >
        <div slot="form-elements">
          <div class="grid-container">
            <!-- Header -->
            <div class="grid-header" style="text-align: left;">
              ${this.i18n.translate('admin.sigmoid.parameter.grid.header.bilanzzone')}
            </div>
            <div class="grid-header">A</div>
            <div class="grid-header">B</div>
            <div class="grid-header">C</div>
            <div class="grid-header">D</div>
            <!-- Rows -->
            ${this.bilanzZonen.map(zone => html`
              <div class="region" style="text-align: center">${zone.bezeichnung}</div>
              ${this._getBilanzZoneParameter(zone)}
            `)}
          </div>
        </div>
      </isceco-form>

      <isceco-dialog
        hidden
        header="${this.i18n.translate('admin.sigmoid.parameter.confirm.header')}"
        id="parameter-confirm-dialog"
        confirm-button="${this.i18n.translate('admin.sigmoid.parameter.confirm.confirm')}"
        Cancel-button="${this.i18n.translate('admin.sigmoid.parameter.confirm.cancel')}"
        @submit="${confirmed => this._close(confirmed)}">
      </isceco-dialog>
      <isceco-dialog id="abort-dialog"
                     hidden
                     header="${this.i18n.translate('admin.confirm.changes.dialog.titel')}"
                     description="${this.i18n.translate('admin.confirm.changes.dialog.beschreibung')}"
                     confirm-button="${this.i18n.translate('admin.confirm.changes.dialog.bestaetigen')}"
                     cancel-button="${this.i18n.translate('admin.confirm.changes.dialog.abbrechen')}"
                     @submit="${event => this.confirmAbort(event)}"
      ></isceco-dialog>
    `
  }

  _close(e) {
    e.target.hidden = true
    if (e.detail.confirmed) {
      this._save()
    }
  }

  _save() {
    const form = document.getElementById("parameter-form")
    const values = form.getValues()
    if (!form.validate()) {
      return Promise.resolve(false)
    }

    const promises = this.bilanzZonen.map(zone => {
      const requestValues = this._getRequestObject(zone, values);
      if (requestValues.id) {
        return this.adminService.update(requestValues, 'sigmoid-parameter')
          .catch(_1 => {
            this.render()
            throw _1  // Ensure the error propagates
          })
      } else {
        return this.adminService.create(requestValues, '/sigmoid-parameter')
          .catch(_1 => {
            this._refresh()
            throw _1  // Ensure the error propagates
          })
      }
    })

    return Promise.all(promises)
      .then(() => {
        // Reset the valueChanged from the form inputs, data are already saved in another way
        const formElements = document.querySelector('#parameter-form').getFormElements();
        if (formElements) {
          formElements.forEach(element => {element.valueChanged = false})
        }
        showAlert(this.i18n.translate('admin.sigmoid.parameter.createupdate.success'), '' , 'success')
        this._refresh()
      })
      .catch(() => {
        // Handle a case where one or more promises were rejected
        showAlert(this.i18n.translate('admin.sigmoid.parameter.createupdate.failure'),'', 'error')
      })
  }

  _getRequestObject(zone, values) {
    const entryIdByZone = document.querySelector(`[name='${zone.code}-ID']`)
    const entryAByZone = Object.entries(values).filter(([key, _]) => key.includes(zone.code+'-A'));
    const entryBByZone = Object.entries(values).filter(([key, _]) => key.includes(zone.code+'-B'));
    const entryCByZone = Object.entries(values).filter(([key, _]) => key.includes(zone.code+'-C'));
    const entryDByZone = Object.entries(values).filter(([key, _]) => key.includes(zone.code+'-D'));
    return {'id': parseInt(entryIdByZone.value), 'codeZone': zone.code, 'a': parseFloat(entryAByZone[0][1]), 'b': parseFloat(entryBByZone[0][1]), 'c': parseFloat(entryCByZone[0][1]), 'd': parseFloat(entryDByZone[0][1])}
  }

  _getBilanzZoneParameter(bilanzzone) {
    const values = this.data.filter(item => item.bilanzZoneCode === bilanzzone.code);
    const value = values[0]
    return html`
          <input type="hidden" name="${bilanzzone.code}-ID" value="${value ? value.id : ''}"/>
          <isceco-text-input required name="${bilanzzone.code}-A" pattern="^-?\\d+(\\.\\d{0,2})?" pattern-error-text="${this.i18n.translate('admin.sigmoid.parameter.grid.bilanzzone.item')}" value="${value ? value.a : ''}"></isceco-text-input>
          <isceco-text-input required name="${bilanzzone.code}-B" pattern="^-\\d+(\\.\\d{0,2})?" pattern-error-text="${this.i18n.translate('admin.sigmoid.parameter.grid.bilanzzone.item.negativ')}" value="${value ? value.b : ''}"></isceco-text-input>
          <isceco-text-input required name="${bilanzzone.code}-C" pattern="^-?\\d+(\\.\\d{0,2})?" pattern-error-text="${this.i18n.translate('admin.sigmoid.parameter.grid.bilanzzone.item')}" value="${value ? value.c : ''}"></isceco-text-input>
          <isceco-text-input required name="${bilanzzone.code}-D" pattern="^-?\\d+(\\.\\d{0,2})?" pattern-error-text="${this.i18n.translate('admin.sigmoid.parameter.grid.bilanzzone.item')}" value="${value ? value.d : ''}"></isceco-text-input>
      `
  }

  handleAbort = event => {
    const dialog = document.querySelector('#abort-dialog')
    dialog.hidden = false
    this.nextUrl = event.detail.url ?? '#Cockpit'
  }

  confirmAbort = event => {
    if (event.detail.confirmed) {
      navigate({to: this.nextUrl, allow: true})
    } else {
      event.target.hidden = true
    }
  }
}
customElements.define('gasmonitoring-frontend-admin-parameter', Parameter)
